// ─────────────────────────────────────────────────────────────────────────────
// i18n/index.ts  —  runtime language switching via React Context
//
// Default language: English ('en')
// To override the initial language at deploy time, set VITE_LANGUAGE=fr in .env
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import en, { Locale } from './en';
import fr from './fr';

export type { Locale } from './en';

const SUPPORTED = { en, fr } as const;
export type SupportedLang = keyof typeof SUPPORTED;

// .env can pre-select a language; falls back to English
function envLang(): SupportedLang {
  const raw = (import.meta.env?.VITE_LANGUAGE as string | undefined) ?? '';
  const code = raw.trim().toLowerCase();
  return (code in SUPPORTED ? code : 'en') as SupportedLang;
}

// ── Context ───────────────────────────────────────────────────────────────────

interface I18nContextValue {
  lang: SupportedLang;
  t: Locale;
  setLang: (lang: SupportedLang) => void;
  toggle: () => void;
}

const I18nContext = createContext<I18nContextValue>({
  lang: 'en',
  t: en,
  setLang: () => {},
  toggle: () => {},
});

// ── Provider — wrap your app root (or just GeoAutomationViewer) ───────────────

export const I18nProvider: React.FC<{
  children: React.ReactNode;
  initialLang?: SupportedLang;
}> = ({ children, initialLang }) => {
  const [lang, setLangState] = useState<SupportedLang>(() => initialLang ?? envLang());

  const setLang = useCallback((next: SupportedLang) => setLangState(next), []);
  const toggle  = useCallback(() => setLangState(l => l === 'fr' ? 'en' : 'fr'), []);

  // useMemo so the locale object reference is stable between re-renders
  const t = useMemo(() => SUPPORTED[lang], [lang]);

  const value = useMemo<I18nContextValue>(
    () => ({ lang, t, setLang, toggle }),
    [lang, t, setLang, toggle]
  );

  return React.createElement(I18nContext.Provider, { value }, children);
};

// ── Hook — used in every component instead of a static import ─────────────────

export function useI18n() {
  return useContext(I18nContext);
}
